import React, { useState } from 'react';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import inventoryApi from '../../../services/inventoryApi';
import { downloadXlsxFromAoa } from '../../../utils/excelJsSheetHelpers';
import SearchableCostCenterDropdown from '../../Common/SearchableCostCenterDropdown';
import {
  ReportShell, Field, DateInput, WarehouseSelect, ProductFilters, EMPTY_PRODUCT, productParams, productFilterLine,
  ExportButtons, ViewToggle, printReport, runReport, qty, money, today, monthStart, fmtDate, esc,
} from './shared/ReportKit';

// Consumption Detail: stock consumed (material requests released) by division (cost center)
const ConsumptionReport = () => {
  const [startDate, setStartDate] = useState(monthStart());
  const [endDate, setEndDate] = useState(today());
  const [division, setDivision] = useState({ id: '', label: '' });
  const [warehouse, setWarehouse] = useState({ id: '', label: '' });
  const [product, setProduct] = useState(EMPTY_PRODUCT);
  const [view, setView] = useState('detail');
  const [open, setOpen] = useState({});
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const generate = () => runReport(() => inventoryApi.getConsumptionReport({
    startDate, endDate, ...(division.id ? { costCenter: division.id } : {}), ...(warehouse.id ? { warehouseId: warehouse.id } : {}), ...productParams(product),
  }), setLoading, (d) => { setOpen({}); setData(d); });
  const reset = () => { setStartDate(monthStart()); setEndDate(today()); setDivision({ id: '', label: '' }); setWarehouse({ id: '', label: '' }); setProduct(EMPTY_PRODUCT); setData(null); };
  const period = data ? `${fmtDate(data.startDate)} — ${fmtDate(data.endDate)}` : '';
  const filtersLine = [division.label && `Division: ${division.label}`, warehouse.label && `Location: ${warehouse.label}`, productFilterLine(product)].filter(Boolean).join(' · ');
  const estNote = data?.estimatedLines ? `* ${data.estimatedLines} line(s) have no cost recorded on the consumption — amount estimated at the item's current unit cost.` : '';

  const exportExcel = async () => {
    const rows = [['CONSUMPTION DETAIL REPORT'], [`Period: ${period}`, view === 'detail' ? 'Detail' : 'Summary'], ...(filtersLine ? [[filtersLine]] : []), []];
    if (view === 'detail') {
      rows.push(['Division Code', 'Division Name', 'Item Code', 'Item Name', 'Qty', 'Amount']);
      data.rows.forEach(d => {
        d.items.forEach(i => rows.push([d.divisionCode, d.divisionName, i.itemCode, i.itemName + (i.estimated ? ' *' : ''), i.quantity, i.amount]));
        rows.push(['', `Total ${d.divisionName}`, '', '', d.quantity, d.amount], []);
      });
    } else {
      rows.push(['Division Code', 'Division Name', 'Items', 'Qty', 'Amount']);
      data.rows.forEach(d => rows.push([d.divisionCode, d.divisionName, d.items.length, d.quantity, d.amount]));
    }
    rows.push(['', 'GRAND TOTAL', '', ...(view === 'detail' ? [''] : []), data.totals.quantity, data.totals.amount]);
    if (estNote) rows.push([], [estNote]);
    await downloadXlsxFromAoa(rows, 'Consumption', `consumption-${data.startDate}-to-${data.endDate}.xlsx`, [{ wch: 14 }, { wch: 32 }, { wch: 18 }, { wch: 36 }, { wch: 12 }, { wch: 16 }]);
  };
  const print = () => printReport({
    title: 'Consumption Detail Report', subtitle: `${period} · ${view === 'detail' ? 'Detail' : 'Summary'}`, filtersLine, note: estNote,
    tableHtml: view === 'detail'
      ? `<table><thead><tr><th>Division Code</th><th>Division Name</th><th>Item Code</th><th>Item Name</th><th class="r">Qty</th><th class="r">Amount</th></tr></thead><tbody>${
        data.rows.map(d => d.items.map(i => `<tr><td>${esc(d.divisionCode)}</td><td>${esc(d.divisionName)}</td><td>${esc(i.itemCode)}</td><td>${esc(i.itemName)}${i.estimated ? ' *' : ''}</td><td class="r">${qty(i.quantity)}</td><td class="r">${money(i.amount)}</td></tr>`).join('')
          + `<tr class="sub"><td></td><td colspan="3">Total ${esc(d.divisionName)}</td><td class="r">${qty(d.quantity)}</td><td class="r">${money(d.amount)}</td></tr>`).join('')
      }<tr class="total"><td></td><td colspan="3">Grand total</td><td class="r">${qty(data.totals.quantity)}</td><td class="r">${money(data.totals.amount)}</td></tr></tbody></table>`
      : `<table><thead><tr><th>Division Code</th><th>Division Name</th><th class="r">Items</th><th class="r">Qty</th><th class="r">Amount</th></tr></thead><tbody>${
        data.rows.map(d => `<tr><td>${esc(d.divisionCode)}</td><td>${esc(d.divisionName)}</td><td class="r">${d.items.length}</td><td class="r">${qty(d.quantity)}</td><td class="r">${money(d.amount)}</td></tr>`).join('')
      }<tr class="total"><td></td><td>Grand total</td><td></td><td class="r">${qty(data.totals.quantity)}</td><td class="r">${money(data.totals.amount)}</td></tr></tbody></table>`,
  });

  return (
    <ReportShell
      title="Consumption Detail Report" subtitle="Stock consumed by division (cost center) — detail and summary" icon={BuildingOffice2Icon}
      loading={loading} onGenerate={generate} onReset={reset}
      filters={<>
        <Field label="Date From"><DateInput value={startDate} onChange={setStartDate} /></Field>
        <Field label="Date To"><DateInput value={endDate} onChange={setEndDate} /></Field>
        <Field label="Division" className="min-w-[220px]">
          <div className="[&>div>button]:h-[38px]! [&>div>button]:rounded-lg! [&>div>button]:bg-gray-50!">
            <SearchableCostCenterDropdown value={division.id} displayLabel={division.label} onChange={(id, label) => setDivision({ id: id || '', label: id ? (label || '') : '' })} placeholder="All divisions" menuPlacement="down" />
          </div>
        </Field>
        <Field label="Location"><WarehouseSelect value={warehouse.id} onChange={(id, label) => setWarehouse({ id, label: id ? label : '' })} /></Field>
        <ProductFilters value={product} onChange={setProduct} />
      </>}
      actions={data && <>
        <div className="flex items-center gap-3">
          <ViewToggle value={view} onChange={setView} options={[{ value: 'detail', label: 'Detail' }, { value: 'summary', label: 'Summary' }]} />
          <span className="text-sm text-gray-600"><b className="text-indigo-600">{period}</b> · {data.totals.divisions} division{data.totals.divisions === 1 ? '' : 's'} · Rs {money(data.totals.amount)}</span>
        </div>
        <ExportButtons onExcel={exportExcel} onPrint={print} />
      </>}
    >
      {data && <>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Division Code</th>
                <th className="text-left px-4 py-2 font-medium">Division Name</th>
                {view === 'detail' ? <><th className="text-left px-4 py-2 font-medium">Item Code</th><th className="text-left px-4 py-2 font-medium">Item Name</th></> : <th className="text-right px-4 py-2 font-medium">Items</th>}
                <th className="text-right px-4 py-2 font-medium">Qty</th>
                <th className="text-right px-4 py-2 font-medium">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400 italic">No consumption for these filters</td></tr>}
              {view === 'detail' ? data.rows.map(d => (
                <React.Fragment key={d.costCenterId}>
                  {d.items.map((i, idx) => (
                    <tr key={i.itemId} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-mono text-xs">{idx === 0 ? d.divisionCode : ''}</td>
                      <td className="px-4 py-2">{idx === 0 ? d.divisionName : ''}</td>
                      <td className="px-4 py-2 font-mono text-xs">{i.itemCode}</td>
                      <td className="px-4 py-2">{i.itemName}{i.estimated && <span className="text-amber-600" title="No cost recorded — estimated at the item's current unit cost"> *</span>}</td>
                      <td className="px-4 py-2 text-right font-mono">{qty(i.quantity)} <span className="text-xs text-gray-400">{i.unit}</span></td>
                      <td className="px-4 py-2 text-right font-mono">{money(i.amount)}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold"><td /><td colSpan={3} className="px-4 py-1.5 text-xs">Total {d.divisionName}</td><td className="px-4 py-1.5 text-right font-mono">{qty(d.quantity)}</td><td className="px-4 py-1.5 text-right font-mono">{money(d.amount)}</td></tr>
                </React.Fragment>
              )) : data.rows.map(d => (
                <React.Fragment key={d.costCenterId}>
                  <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setOpen(o => ({ ...o, [d.costCenterId]: !o[d.costCenterId] }))}>
                    <td className="px-4 py-2 font-mono text-xs"><span className="text-indigo-500 mr-1">{open[d.costCenterId] ? '▾' : '▸'}</span>{d.divisionCode}</td>
                    <td className="px-4 py-2">{d.divisionName}</td>
                    <td className="px-4 py-2 text-right">{d.items.length}</td>
                    <td className="px-4 py-2 text-right font-mono">{qty(d.quantity)}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold">{money(d.amount)}</td>
                  </tr>
                  {open[d.costCenterId] && d.items.map(i => (
                    <tr key={i.itemId} className="bg-indigo-50/40 text-xs text-gray-600">
                      <td /><td className="px-4 py-1.5 pl-8">{i.itemCode} — {i.itemName}{i.estimated ? ' *' : ''}</td><td />
                      <td className="px-4 py-1.5 text-right font-mono">{qty(i.quantity)}</td><td className="px-4 py-1.5 text-right font-mono">{money(i.amount)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              {data.rows.length > 0 && (
                <tr className="bg-gray-100 font-bold"><td /><td colSpan={view === 'detail' ? 3 : 2} className="px-4 py-2">Grand total</td><td className="px-4 py-2 text-right font-mono">{qty(data.totals.quantity)}</td><td className="px-4 py-2 text-right font-mono">{money(data.totals.amount)}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {estNote && <p className="text-xs text-amber-700">{estNote}</p>}
      </>}
    </ReportShell>
  );
};

export default ConsumptionReport;
